import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Database, Sparkles } from "lucide-react";

function Labeled({ label, children, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/35 mb-1">{label}</div>
            {children}
        </div>
    );
}

/** Summary, key topics, extracted families (EDC) and the text excerpt of one ingested file version. */
export default function IngestFileDetail({ file }) {
    const [showText, setShowText] = useState(false);
    const fw = file.framework;
    return (
        <div className="px-5 pb-4 space-y-3 text-xs" data-testid={`ingest-file-detail-${file.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                <Labeled label="Summary" testId={`ingest-file-summary-${file.id}`}>
                    <div className="text-white/75 leading-relaxed">{file.summary || <span className="text-white/35 italic">{file.ai?.error ? `No summary — ${file.ai.error}` : "No summary."}</span>}</div>
                </Labeled>
                <div className="space-y-3">
                    {file.key_topics?.length > 0 && (
                        <Labeled label={fw ? "Form families" : "Key topics"} testId={`ingest-file-topics-${file.id}`}>
                            <div className="flex flex-wrap gap-1">{file.key_topics.map((t, i) => <span key={i} className="px-1.5 py-0.5 rounded border border-white/15 bg-white/5 text-white/70 text-[11px]">{t}</span>)}</div>
                        </Labeled>
                    )}
                    <Labeled label="Record">
                        <div className="text-white/50 space-y-0.5">
                            {file.study_id && <div>study <span className="font-mono text-white/70">{file.study_id}</span></div>}
                            {file.protocol_number && <div>protocol <span className="font-mono text-white/70">{file.protocol_number}</span></div>}
                            {file.edc_system && <div>EDC <span className="text-white/70">{file.edc_system}</span></div>}
                            <div>context <span className="font-mono text-white/70">{(file.contexts || []).join(", ")}</span></div>
                            {file.extracted_chars != null && <div>{file.extracted_chars.toLocaleString()} characters extracted{file.extraction_method ? ` · ${file.extraction_method}` : ""}</div>}
                            {file.ai?.model && <div className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#FFB000]" strokeWidth={1.75} />{file.ai.model}{file.ai.duration_ms ? ` · ${(file.ai.duration_ms / 1000).toFixed(1)} s` : ""}</div>}
                            {file.datalake_item_id && <div className="flex items-center gap-1"><Database className="w-3 h-3" strokeWidth={1.75} /><Link to={`/datalake?q=${encodeURIComponent(file.title || "")}`} className="underline" data-testid={`ingest-file-datalake-${file.id}`}>Data Lake entry</Link></div>}
                            {file.framework_id && <div>framework <span className="font-mono text-white/70">{file.framework_id.slice(0, 8)}…</span> · <Link to="/qa/form-generation" className="underline">generate forms</Link></div>}
                            {file.notes && <div className="text-white/60 italic">“{file.notes}”</div>}
                        </div>
                    </Labeled>
                </div>
            </div>
            {fw && (
                <Labeled label={`Extracted families (${fw.family_count ?? fw.families?.length ?? 0})`} testId={`ingest-file-families-${file.id}`}>
                    {(fw.families || []).length === 0 ? <div className="text-amber-300">No form families extracted.</div> : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                            {fw.families.map((f, i) => (
                                <div key={i} className="flex items-center gap-2 px-2 py-1 rounded border border-white/10 bg-white/[0.02]">
                                    <span className="text-white/80 truncate">{f.name}</span>
                                    {f.cdisc_domain && <span className="font-mono text-[10px] text-violet-300">{f.cdisc_domain}</span>}
                                    <span className="ml-auto text-white/40">{f.field_count} field{f.field_count === 1 ? "" : "s"}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Labeled>
            )}
            {file.excerpt && (
                <div>
                    <button type="button" onClick={() => setShowText(!showText)} className="flex items-center gap-1 text-white/55 hover:text-white" data-testid={`ingest-file-excerpt-toggle-${file.id}`}>
                        {showText ? <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} /> : <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />}Text excerpt
                    </button>
                    {showText && <pre className="mt-1 p-3 rounded border border-white/10 bg-black text-[11px] text-white/60 whitespace-pre-wrap max-h-64 overflow-y-auto font-mono" data-testid={`ingest-file-excerpt-${file.id}`}>{file.excerpt}</pre>}
                </div>
            )}
        </div>
    );
}
